import React from "react";
import { PrayerTimes } from "../../types/PrayerTypes";
import { ScreenData } from "../../types/ScreenTypes";
import { nextPrayerStep, toMinutes } from "../../utils/nextPrayerStep";
import { applyPrayerNameTokens } from "../../utils/prayerNames";
import { formatTimeDisplay, getCurrentTimeInTimezone } from "../../utils/timeUtils";

// The next salah on its own, like the Masjid Clock's simple view: name, a Begins card and a
// Jama'ah card (one lit at a time) and a countdown. Cards are side by side in a long, low box,
// rows in a squarer one. Colours are --home-* variables so Home's black-and-white look can swap
// them; the fallbacks are Home's own dark palette.
const C = {
  card: "var(--home-card, #1B1E24)",
  edge: "var(--home-card-edge, #262A31)",
  text: "var(--home-text, #F4F1EA)",
  label: "var(--home-label, #B4BAC3)",
  soft: "var(--home-soft, #C9CDD3)",
  muted: "var(--home-muted, #9AA1AB)",
  gold: "var(--home-gold, #D9B36C)",
  lit: "var(--home-lit, var(--masjid-colour, #14704C))",
  litInk: "var(--home-lit-ink, var(--masjid-colour-ink, #FFFFFF))",
  stripe: "var(--home-stripe, transparent)",
  litStripe: "var(--home-lit-stripe, transparent)",
};

const ORDINALS = ["1st", "2nd", "3rd", "4th"];

interface Props {
  x: number;
  y: number;
  width: number;
  height: number;
  fontFamily: string;
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes?: PrayerTimes | null;
  screenData: ScreenData;
}

const pad = (n: number) => String(n).padStart(2, "0");

const NextPrayerBlock: React.FC<Props> = ({
  x,
  y,
  width,
  height,
  fontFamily,
  prayerTimes,
  tomorrowPrayerTimes,
  screenData,
}) => {
  const now = getCurrentTimeInTimezone(screenData.timeZone);
  const nowSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  const step = nextPrayerStep(
    prayerTimes,
    tomorrowPrayerTimes,
    Math.floor(nowSeconds / 60),
    now.getDay() === 5,
  );
  if (!step) return null;

  const names = screenData.customizedNames;
  const name = (token: string) => applyPrayerNameTokens(`{${token}}`, names);
  const jamaat = name("Jamaat");
  const title = {
    fajr: name("Fajr"),
    sunrise: name("Sunrise"),
    jummah: name("Jummah"),
    dhuhr: name("Dhuhr"),
    asar: name("Asar"),
    maghrib: name("Maghrib"),
    isha: name("Isha"),
  }[step.key];
  const time = (t?: string) =>
    formatTimeDisplay(t, screenData.use12HourFormat ?? true).replace(/\s*(am|pm)$/i, "");

  // Counts to the jama'ah (to sunrise on the Sunrise step), then reads "Now" until the step moves on.
  const sunriseStep = step.key === "sunrise";
  const target = toMinutes(sunriseStep ? step.begins : step.jamaah);
  const left = target === null ? null : target * 60 + (step.tomorrow ? 86400 : 0) - nowSeconds;
  const what = sunriseStep ? `${name("Fajr")} ends` : step.key === "jummah" ? title : `${title} ${jamaat}`;
  const countdownLabel = left !== null && left > 0 ? `${what} in` : what;
  const countdown =
    left === null ? "" : left > 0
      ? `${pad(Math.floor(left / 3600))}:${pad(Math.floor((left % 3600) / 60))}:${pad(left % 60)}`
      : "Now";

  const jamaahLabel =
    step.key === "jummah" && (step.slots ?? 1) > 1 ? `${ORDINALS[(step.slot ?? 1) - 1]} ${jamaat}` : jamaat;
  const cards = sunriseStep
    ? [{ label: title, value: time(step.begins), lit: false }]
    : [
        { label: "Begins", value: time(step.begins), lit: step.beginsLit },
        { label: jamaahLabel, value: time(step.jamaah), lit: step.jamaahLit },
      ];

  // The landscape layouts give a long, low box (about 3:1); the portrait ones a near-square one.
  const wide = width > height * 1.6;
  // Sizes are in design pixels, scaled from the 16:10 tablet layouts' boxes.
  const u = wide ? height / 560 : width / 1072;
  const px = (n: number) => `${n * u}px`;

  const nextLabel = (
    <div style={{ display: "flex", gap: px(16), fontWeight: 700, fontSize: px(34), letterSpacing: "0.14em", color: C.muted, textTransform: "uppercase" }}>
      <span>Next</span>
      {step.tomorrow && <span style={{ color: C.gold, letterSpacing: "0.1em" }}>· Tomorrow</span>}
    </div>
  );
  const nameSize = title.length <= 4 ? 200 : title.length <= 6 ? 172 : 140;

  const cardBox = (card: (typeof cards)[number], i: number) => {
    const value = card.value;
    return (
      <div
        key={i}
        style={{
          flex: 1,
          minWidth: 0,
          borderRadius: px(12),
          background: card.lit ? C.lit : C.card,
          border: `${px(2)} solid ${card.lit ? C.lit : C.edge}`,
          boxShadow: `inset ${px(18)} 0 0 ${card.lit ? C.litStripe : C.stripe}`,
          color: card.lit ? C.litInk : C.text,
          display: "flex",
          flexDirection: wide ? "column" : "row",
          alignItems: "center",
          justifyContent: wide ? "center" : "space-between",
          gap: px(10),
          padding: wide ? 0 : `0 ${px(48)} 0 ${px(60)}`,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: px(wide ? 40 : 42), letterSpacing: "0.1em", textTransform: "uppercase", color: card.lit ? C.litInk : C.label }}>
          {card.label}
        </span>
        <span style={{ fontWeight: 800, lineHeight: 1, fontSize: px(wide ? (value.length >= 5 ? 162 : 200) : 140) }}>{value}</span>
      </div>
    );
  };

  const countdownBlock = countdown && (
    <div style={wide ? undefined : { display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
      <div style={{ fontWeight: 700, fontSize: px(40), color: C.soft }}>{countdownLabel}</div>
      <div style={{ fontWeight: 800, fontSize: px(wide ? 128 : 120), lineHeight: 1.05, color: C.gold }}>{countdown}</div>
    </div>
  );

  return (
    <div
      style={{
        position: "absolute",
        left: `${x}px`,
        top: `${y}px`,
        width: `${width}px`,
        height: `${height}px`,
        fontFamily,
        color: C.text,
        fontVariantNumeric: "tabular-nums",
        userSelect: "none",
        display: "grid",
        ...(wide
          ? { gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.6fr)", gap: px(56), alignItems: "center" }
          : { alignContent: "start", gap: px(22) }),
      }}
    >
      {wide ? (
        <>
          <div>
            {nextLabel}
            <div style={{ fontWeight: 800, fontSize: px(nameSize), lineHeight: 1.05, margin: `${px(6)} 0 ${px(40)}`, whiteSpace: "nowrap" }}>
              {title}
            </div>
            {countdownBlock}
          </div>
          <div style={{ display: "flex", gap: px(28), height: `${height}px` }}>{cards.map(cardBox)}</div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 800, fontSize: px(Math.min(150, nameSize)), lineHeight: 1.05, whiteSpace: "nowrap" }}>{title}</div>
            {nextLabel}
          </div>
          <div style={{ display: "grid", gap: px(20) }}>
            {cards.map((card, i) => (
              <div key={i} style={{ display: "flex", height: px(200) }}>
                {cardBox(card, i)}
              </div>
            ))}
          </div>
          <div style={{ marginTop: px(24) }}>{countdownBlock}</div>
        </>
      )}
    </div>
  );
};

export default NextPrayerBlock;
